<template>
    <div :class="['scope-picker', { 'has-error': error }]">
        <div class="scope-picker__toolbar">
            <input
                v-model.trim="query"
                type="search"
                class="form-input scope-picker__search"
                :placeholder="kind === 'partner' ? 'Найти партнёра' : 'Найти рекламодателя или партнёра'"
                :disabled="disabled"
            />
            <span class="scope-picker__counter">Выбрано {{ selected.length }} из {{ totalCount }}</span>
        </div>

        <div class="scope-picker__bulk">
            <button type="button" class="scope-picker__link" :disabled="disabled || !visibleIds.length" @click="selectVisible">
                Выбрать {{ query ? 'найденные' : 'все' }}
            </button>
            <button type="button" class="scope-picker__link" :disabled="disabled || !selected.length" @click="selected = []">
                Снять выбор
            </button>
        </div>

        <div class="scope-picker__list" role="group" :aria-label="kind === 'partner' ? 'Партнёры' : 'Рекламодатели'">
            <template v-if="kind === 'partner'">
                <label v-for="partner in visiblePartners" :key="partner.id" class="scope-option">
                    <input type="checkbox" :checked="isSelected(partner.id)" :disabled="disabled" @change="toggle(partner.id)" />
                    <span class="scope-option__name">{{ partner.name }}</span>
                    <span class="scope-option__meta">{{ advertiserCountByPartner.get(Number(partner.id)) || 0 }} рекл.</span>
                </label>
            </template>

            <template v-else>
                <div v-for="group in visibleGroups" :key="group.key" class="scope-group">
                    <label class="scope-group__header">
                        <input
                            type="checkbox"
                            :checked="groupState(group) === 'all'"
                            :indeterminate.prop="groupState(group) === 'some'"
                            :disabled="disabled"
                            @change="toggleGroup(group)"
                        />
                        <span class="scope-group__name">{{ group.name }}</span>
                        <span class="scope-option__meta">{{ selectedInGroup(group) }} / {{ group.advertisers.length }}</span>
                    </label>
                    <label v-for="advertiser in group.advertisers" :key="advertiser.id" class="scope-option scope-option--nested">
                        <input type="checkbox" :checked="isSelected(advertiser.id)" :disabled="disabled" @change="toggle(advertiser.id)" />
                        <span class="scope-option__name">{{ advertiser.name }}</span>
                    </label>
                </div>
            </template>

            <p v-if="!visibleIds.length" class="scope-picker__empty">
                {{ query ? 'Ничего не найдено' : emptyText }}
            </p>
        </div>

        <p v-if="error" class="scope-picker__error" role="alert">{{ error }}</p>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    kind: { type: String, required: true, validator: value => ['partner', 'advertiser'].includes(value) },
    partners: { type: Array, required: true },
    advertisers: { type: Array, required: true },
    error: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
});

const selected = defineModel({ type: Array, required: true });

const query = ref('');

const byName = (a, b) => a.name.localeCompare(b.name);
const includesQuery = text => text.toLowerCase().includes(query.value.toLowerCase());

const emptyText = computed(() => (props.kind === 'partner'
    ? 'Вам не доступен ни один партнёр'
    : 'Вам не доступен ни один рекламодатель'));

const selectedSet = computed(() => new Set(selected.value.map(Number)));
const isSelected = id => selectedSet.value.has(Number(id));

const toggle = (id) => {
    const numericId = Number(id);
    selected.value = isSelected(numericId)
        ? selected.value.filter(value => Number(value) !== numericId)
        : [...selected.value, numericId];
};

const advertiserCountByPartner = computed(() => props.advertisers.reduce((counts, advertiser) => {
    const partnerId = Number(advertiser.partner_id);
    counts.set(partnerId, (counts.get(partnerId) || 0) + 1);
    return counts;
}, new Map()));

const visiblePartners = computed(() => [...props.partners]
    .filter(partner => includesQuery(partner.name))
    .sort(byName));

/** Рекламодатели по партнёрам; при поиске по имени партнёра показываются все его рекламодатели. */
const visibleGroups = computed(() => {
    const partnersById = new Map(props.partners.map(partner => [Number(partner.id), partner]));
    const groups = new Map();

    props.advertisers.forEach((advertiser) => {
        const partnerId = advertiser.partner_id == null ? null : Number(advertiser.partner_id);
        const partnerName = partnersById.get(partnerId)?.name || advertiser.partner?.name || 'Без партнёра';
        const key = partnerId ?? 'none';

        if (!groups.has(key)) groups.set(key, { key, name: partnerName, advertisers: [] });
        if (includesQuery(advertiser.name) || includesQuery(partnerName)) groups.get(key).advertisers.push(advertiser);
    });

    return [...groups.values()]
        .filter(group => group.advertisers.length)
        .map(group => ({ ...group, advertisers: group.advertisers.sort(byName) }))
        .sort(byName);
});

const visibleIds = computed(() => (props.kind === 'partner'
    ? visiblePartners.value.map(partner => Number(partner.id))
    : visibleGroups.value.flatMap(group => group.advertisers.map(advertiser => Number(advertiser.id)))));

const totalCount = computed(() => (props.kind === 'partner' ? props.partners.length : props.advertisers.length));

const selectVisible = () => {
    selected.value = [...new Set([...selected.value.map(Number), ...visibleIds.value])];
};

const selectedInGroup = group => group.advertisers.filter(advertiser => isSelected(advertiser.id)).length;

const groupState = (group) => {
    const count = selectedInGroup(group);
    if (count === 0) return 'none';
    return count === group.advertisers.length ? 'all' : 'some';
};

const toggleGroup = (group) => {
    const ids = group.advertisers.map(advertiser => Number(advertiser.id));
    if (groupState(group) === 'all') {
        const groupIds = new Set(ids);
        selected.value = selected.value.filter(id => !groupIds.has(Number(id)));
    } else {
        selected.value = [...new Set([...selected.value.map(Number), ...ids])];
    }
};
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;

.scope-picker {
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;
}

.scope-picker__toolbar {
    display: flex;
    align-items: center;
    gap: $spacing-md;
}

.scope-picker__search {
    flex: 1;
    padding: 0.4rem $spacing-md;
}

.scope-picker__counter {
    flex-shrink: 0;
    font-size: $font-size-sm;
    color: $admin-text-muted;
}

.scope-picker__bulk {
    display: flex;
    gap: $spacing-lg;
}

.scope-picker__link {
    padding: 0;
    border: none;
    background: none;
    font-size: $font-size-sm;
    color: $admin-primary-color;
    cursor: pointer;

    &:hover:not(:disabled) { text-decoration: underline; }
}

.scope-picker__list {
    max-height: 360px;
    overflow-y: auto;
    padding: $spacing-xs;
    border: 1px solid $admin-border-color;
    border-radius: $radius-md;
    background: $admin-bg-secondary;

    .has-error & { border-color: $admin-danger-color; }
}

.scope-option,
.scope-group__header {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    padding: $spacing-sm $spacing-md;
    border-radius: $radius-sm;
    font-size: $font-size-base;
    cursor: pointer;

    &:hover { background: $admin-bg-primary; }

    input { flex-shrink: 0; }
}

.scope-option--nested {
    padding-left: 2.25rem;
}

.scope-group + .scope-group {
    margin-top: $spacing-xs;
    border-top: 1px solid $admin-border-color;
    padding-top: $spacing-xs;
}

.scope-group__name {
    flex: 1;
    font-weight: 600;
    color: $admin-text-primary;
}

.scope-option__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $admin-text-primary;
}

.scope-option__meta {
    flex-shrink: 0;
    font-size: $font-size-xs;
    color: $admin-text-muted;
}

.scope-picker__empty {
    margin: 0;
    padding: $spacing-lg;
    text-align: center;
    font-size: $font-size-sm;
    color: $admin-text-light;
}

.scope-picker__error {
    margin: 0;
    font-size: $font-size-sm;
    color: $admin-danger-color;
}
</style>
